import * as React from "react";
import { Box, Paper, PaperProps, useTheme, alpha, useMediaQuery, List, Typography, Divider, Chip } from "@mui/material";
import { DataGridProps, GridColDef } from "@mui/x-data-grid";
import DataGridCompat from "./DataGridCompat";
import ListItem from "./ListItem";

export interface GlassDataGridProps extends Omit<Partial<DataGridProps> & any, "sx"> {
  height?: number | string;
  sx?: (Partial<DataGridProps> & any)["sx"];
  paperSx?: PaperProps["sx"];
  renderMobileList?: (rows: any[]) => React.ReactNode;
  enableColumnResize?: boolean; // Enable custom column resizing (default: true)
}

const GlassDataGrid = React.forwardRef<HTMLDivElement, GlassDataGridProps>(
  ({ height = "70vh", sx, paperSx, renderMobileList, enableColumnResize = true, ...dataGridProps }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Custom column resizing state
    const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({});
    const [isResizing, setIsResizing] = React.useState(false);
    const isResizingRef = React.useRef(false); // For fast access without re-render
    const resizeRef = React.useRef<{
      columnField: string | null;
      startX: number;
      startWidth: number;
      rafId: number | null;
      columnElement: HTMLElement | null;
    }>({ 
      columnField: null, 
      startX: 0, 
      startWidth: 0,
      rafId: null,
      columnElement: null,
    });
    const gridRef = React.useRef<HTMLDivElement>(null);
    const pendingWidthRef = React.useRef<Record<string, number>>({});

    // Helper function to get cell value - simplified version
    const getCellValue = React.useCallback((row: any, column: GridColDef) => {
      const field = column.field;
      if (!field) return "";

      // Try to get value from row directly first
      const rawValue = row[field];

      // If column has renderCell, we can't easily get the formatted value
      // So we'll just return the raw value or try to format it
      if (rawValue === null || rawValue === undefined) return "-";

      return rawValue;
    }, []);

    // Auto-generate mobile list view from columns and rows
    const renderAutoMobileList = React.useCallback((rows: any[], columns: GridColDef[]) => {
      if (!rows || rows.length === 0) {
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </Box>
        );
      }

      // Get primary column (first visible column or first column)
      // Check hide property safely
      const primaryColumn = columns.find(col => {
        if (!col.field) return false;
        const colAny = col as any;
        return !colAny.hide;
      }) || columns[0];

      // Get secondary columns (limit to 2-3 most important)
      const secondaryColumns = columns
        .filter(col => {
          if (!col.field || col.field === primaryColumn?.field) return false;
          const colAny = col as any;
          return !colAny.hide;
        })
        .slice(0, 3);

      return (
        <List sx={{ py: 0 }}>
          {rows.map((row, index) => {
            const primaryValue = primaryColumn ? getCellValue(row, primaryColumn) : "";
            const primaryLabel = primaryColumn?.headerName || primaryColumn?.field || "";

            return (
              <React.Fragment key={row.id || index}>
                <ListItem
                  title={primaryLabel}
                  subtitle={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {String(primaryValue || "-")}
                      </Typography>
                      {secondaryColumns.map((col) => {
                        const value = getCellValue(row, col);
                        if (!value && value !== 0) return null;
                        return (
                          <Box key={col.field} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {col.headerName || col.field}:
                            </Typography>{" "}
                            <Typography variant="caption" color="text.primary" component="span">
                              {String(value)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  }
                />
                {index < rows.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      );
    }, [getCellValue]);

    // Apply custom column widths to columns - only update when not resizing
    const columnsWithResize = React.useMemo(() => {
      if (!dataGridProps.columns || !enableColumnResize) return dataGridProps.columns;
      
      // Don't recompute during resize - use only saved widths
      const widths = isResizingRef.current ? columnWidths : { ...columnWidths, ...pendingWidthRef.current };
      
      return dataGridProps.columns.map((col: GridColDef) => {
        if (col.field && widths[col.field]) {
          return { ...col, width: widths[col.field], flex: undefined };
        }
        return col;
      });
    }, [dataGridProps.columns, columnWidths, enableColumnResize]);

    // Custom column resizing logic
    React.useEffect(() => {
      if (!enableColumnResize) return;

      const handleMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const separator = target.closest('.MuiDataGrid-columnSeparator');
        if (!separator) return;

        const columnHeader = separator.closest('.MuiDataGrid-columnHeader');
        if (!columnHeader) return;

        // Get column field from data-field attribute (MUI DataGrid standard)
        let columnField = columnHeader.getAttribute('data-field');

        // If no data-field, try to get from aria-colindex and match with columns array
        if (!columnField && columnsWithResize) {
          const colIndex = columnHeader.getAttribute('aria-colindex');
          if (colIndex) {
            const index = parseInt(colIndex) - 1; // aria-colindex is 1-based
            if (index >= 0 && index < columnsWithResize.length && columnsWithResize[index]?.field) {
              columnField = columnsWithResize[index].field || undefined;
            }
          }
        }

        // Last resort: try to match by header text
        if (!columnField && columnsWithResize) {
          const headerTitle = columnHeader.querySelector('.MuiDataGrid-columnHeaderTitle');
          if (headerTitle) {
            const headerText = headerTitle.textContent?.trim();
            const matchedCol = columnsWithResize.find((col: GridColDef) => col.headerName === headerText);
            if (matchedCol?.field) {
              columnField = matchedCol.field;
            }
          }
        }

        if (!columnField) return;

        const columnElement = columnHeader as HTMLElement;
        const currentWidth = columnElement.offsetWidth;
        const startX = e.clientX;

        // Cancel any pending RAF
        if (resizeRef.current.rafId !== null) {
          cancelAnimationFrame(resizeRef.current.rafId);
        }

        resizeRef.current = {
          columnField,
          startX,
          startWidth: currentWidth,
          rafId: null,
          columnElement,
        };

        pendingWidthRef.current = {};
        isResizingRef.current = true;
        setIsResizing(true);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        separator.classList.add('MuiDataGrid-columnSeparator--resizing');
        e.preventDefault();
        e.stopPropagation();
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isResizingRef.current || !resizeRef.current.columnField) return;

        const diff = e.clientX - resizeRef.current.startX;
        const newWidth = Math.max(50, resizeRef.current.startWidth + diff);
        const columnField = resizeRef.current.columnField;
        const widthStr = `${newWidth}px`;

        // Update DOM directly for immediate visual feedback (no re-render, no RAF delay)
        // This is the fastest way - direct DOM manipulation
        if (resizeRef.current.columnElement) {
          const el = resizeRef.current.columnElement;
          el.style.width = widthStr;
          el.style.minWidth = widthStr;
          el.style.maxWidth = widthStr;
        }

        // Cache grid container to avoid repeated queries
        const gridContainer = gridRef.current?.querySelector('.MuiDataGrid-root') || 
                              document.querySelector('.MuiDataGrid-root');
        
        if (gridContainer) {
          // Update column header (if not already updated above)
          const header = gridContainer.querySelector(`.MuiDataGrid-columnHeader[data-field="${columnField}"]`) as HTMLElement;
          if (header && header !== resizeRef.current.columnElement) {
            header.style.width = widthStr;
            header.style.minWidth = widthStr;
            header.style.maxWidth = widthStr;
          }

          // Update all visible cells in this column - only update what's visible
          // Use more efficient query and batch update
          const cells = gridContainer.querySelectorAll(`.MuiDataGrid-cell[data-field="${columnField}"]`);
          const cellCount = cells.length;
          
          // For large datasets, only update visible cells (optimization)
          // But for smoothness, update all cells
          for (let i = 0; i < cellCount; i++) {
            const cellEl = cells[i] as HTMLElement;
            cellEl.style.width = widthStr;
            cellEl.style.minWidth = widthStr;
            cellEl.style.maxWidth = widthStr;
          }
        }

        // Store pending width for state update (but don't update state during resize)
        pendingWidthRef.current[columnField] = newWidth;

        // Don't update state during resize - only visual DOM updates
        // State will be updated on mouseup for persistence
      };

      const handleMouseUp = () => {
        if (!isResizingRef.current) return;

        // Cancel pending RAF
        if (resizeRef.current.rafId !== null) {
          cancelAnimationFrame(resizeRef.current.rafId);
          resizeRef.current.rafId = null;
        }

        // Final state update with all pending widths (only once at the end)
        if (Object.keys(pendingWidthRef.current).length > 0) {
          // Use requestAnimationFrame to ensure smooth transition
          requestAnimationFrame(() => {
            setColumnWidths((prev) => ({
              ...prev,
              ...pendingWidthRef.current,
            }));
            
            // Clear inline styles after state update so DataGrid can take over
            const columnField = resizeRef.current.columnField;
            if (columnField) {
              // Remove inline styles from header
              if (resizeRef.current.columnElement) {
                resizeRef.current.columnElement.style.width = '';
                resizeRef.current.columnElement.style.minWidth = '';
                resizeRef.current.columnElement.style.maxWidth = '';
              }

              // Remove inline styles from all cells
              const allCells = document.querySelectorAll(
                `[data-field="${columnField}"], .MuiDataGrid-cell[data-colindex="${columnField}"]`
              );
              allCells.forEach((cell) => {
                const cellEl = cell as HTMLElement;
                cellEl.style.width = '';
                cellEl.style.minWidth = '';
                cellEl.style.maxWidth = '';
              });
            }
            
            pendingWidthRef.current = {};
          });
        } else {
          // No pending widths, just reset styles
          if (resizeRef.current.columnElement) {
            resizeRef.current.columnElement.style.width = '';
            resizeRef.current.columnElement.style.minWidth = '';
            resizeRef.current.columnElement.style.maxWidth = '';
          }
        }

        isResizingRef.current = false;
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        const separators = document.querySelectorAll('.MuiDataGrid-columnSeparator--resizing');
        separators.forEach((sep) => sep.classList.remove('MuiDataGrid-columnSeparator--resizing'));

        resizeRef.current = { 
          columnField: null, 
          startX: 0, 
          startWidth: 0,
          rafId: null,
          columnElement: null,
        };
      };

      // Use passive listeners for better performance
      document.addEventListener('mousedown', handleMouseDown, { passive: false });
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp, { passive: true });

      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        // Cleanup any pending RAF
        if (resizeRef.current.rafId !== null) {
          cancelAnimationFrame(resizeRef.current.rafId);
        }
      };
    }, [isResizing, columnsWithResize, enableColumnResize]);

    // Conditional rendering: show mobile list if on mobile and has rows
    // if (isMobile && dataGridProps.rows && dataGridProps.columns) {
    //   const rows = dataGridProps.rows || [];
    //   const columns = dataGridProps.columns || [];

    //   return (
    //     <Paper
    //       ref={ref}
    //       sx={{
    //         height,
    //         position: "relative",
    //         overflow: "hidden",
    //         borderRadius: "12px",
    //         isolation: "isolate",
    //         background: isDark
    //           ? `linear-gradient(135deg, ${alpha("#0d1b2a", 0.92)} 0%, ${alpha("#0d1b2a", 0.85)} 40%, ${alpha("#12903C", 0.65)} 100%)`
    //           : `linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(18, 144, 60, 0.35) 100%)`,
    //         backdropFilter: "blur(24px) saturate(140%)",
    //         WebkitBackdropFilter: "blur(24px) saturate(140%)",
    //         border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
    //         boxShadow: isDark
    //           ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
    //           : "0 8px 32px rgba(18, 144, 60, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
    //         "&::before": {
    //           content: '""',
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           bottom: 0,
    //           borderRadius: "12px",
    //           pointerEvents: "none",
    //           boxShadow: isDark
    //             ? "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)"
    //             : "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
    //           zIndex: 1,
    //         },
    //         ...paperSx,
    //       }}
    //     >
    //       {/* Gradient overlays for glass shine effect */}
    //       <Box
    //         sx={{
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           height: "50%",
    //           background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
    //           pointerEvents: "none",
    //           zIndex: 1,
    //           borderRadius: "12px 12px 0 0",
    //         }}
    //       />
    //       <Box
    //         sx={{
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           bottom: 0,
    //           background: `linear-gradient(135deg, transparent 0%, ${alpha("#12903C", 0.03)} 100%)`,
    //           pointerEvents: "none",
    //           zIndex: 1,
    //           borderRadius: "12px",
    //         }}
    //       />
    //       <Box
    //         sx={{
    //           position: "relative",
    //           zIndex: 2,
    //           height: "100%",
    //           overflow: "auto",
    //           p: 2,
    //         }}
    //       >
    //         {renderMobileList
    //           ? renderMobileList(rows)
    //           : renderAutoMobileList(rows, columns)
    //         }
    //       </Box>
    //     </Paper>
    //   );
    // }

    return (
      <Paper
        ref={ref}
        sx={{
          height,
          position: "relative",
          overflow: "hidden",
          borderRadius: "12px",
          isolation: "isolate",
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#0d1b2a", 0.92)} 0%, ${alpha("#0d1b2a", 0.85)} 40%, ${alpha("#F4A259", 0.65)} 100%)`
            : `linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(244, 162, 89, 0.35) 100%)`,
          backdropFilter: "blur(24px) saturate(140%)",
          WebkitBackdropFilter: "blur(24px) saturate(140%)",
          border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
          boxShadow: isDark
            ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
            : "0 8px 32px rgba(244, 162, 89, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "12px",
            pointerEvents: "none",
            boxShadow: isDark
              ? "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)"
              : "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
            zIndex: 1,
          },
          ...paperSx,
        }}
      >
        {/* Gradient overlays for glass shine effect */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "50%",
            background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: "12px 12px 0 0",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, transparent 0%, ${alpha("#F4A259", 0.03)} 100%)`,
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: "12px",
          }}
        />
        <DataGridCompat
          {...dataGridProps}
          columns={columnsWithResize}
          ref={gridRef}
          sx={{
            border: "none",
            position: "relative",
            zIndex: 2,
            // Ensure pointer events work
            pointerEvents: "auto",
            // Hide column headers when there's no data
            "& .MuiDataGrid-columnHeaders": {
              display: (!dataGridProps.rows || dataGridProps.rows.length === 0) ? "none" : "flex",
            },
            // Ensure column separator is visible and interactive
            "& .MuiDataGrid-columnSeparator": {
              display: "flex !important",
              visibility: "visible !important",
              opacity: enableColumnResize ? "0.6 !important" : "0.3 !important",
              cursor: enableColumnResize ? "col-resize !important" : "default !important",
              pointerEvents: enableColumnResize ? "auto !important" : "none !important",
              position: "relative",
              zIndex: 100,
              width: "8px !important",
              minWidth: "8px !important",
              marginRight: "-4px !important",
              "&:hover": {
                opacity: enableColumnResize ? "1 !important" : "0.3 !important",
                backgroundColor: enableColumnResize ? "rgba(244, 162, 89, 0.2) !important" : "transparent !important",
              },
              "&.MuiDataGrid-columnSeparator--resizing": {
                opacity: "1 !important",
                backgroundColor: "rgba(244, 162, 89, 0.4) !important",
              },
              // Ensure button inside can receive events
              "& button": {
                pointerEvents: enableColumnResize ? "auto !important" : "none !important",
                cursor: enableColumnResize ? "col-resize !important" : "default !important",
                width: "100%",
                height: "100%",
                border: "none",
                background: "transparent",
              },
            },
            // Ensure column header allows resizing - optimize for performance
            "& .MuiDataGrid-columnHeader": {
              position: "relative",
              willChange: isResizing && enableColumnResize ? "width" : "auto",
              "&:hover .MuiDataGrid-columnSeparator": {
                opacity: enableColumnResize ? "0.8 !important" : "0.3 !important",
                visibility: "visible !important",
              },
            },
            // Optimize cells during resize
            "& .MuiDataGrid-cell": {
              willChange: isResizing && enableColumnResize ? "width" : "auto",
            },
            ...(isResizing && enableColumnResize ? {
              "& .MuiDataGrid-columnHeader": {
                userSelect: "none",
                transition: "none !important", // Disable transitions during resize for smoothness
              },
              "& .MuiDataGrid-cell": {
                transition: "none !important", // Disable transitions during resize
              },
              "& *": {
                cursor: "col-resize !important",
              },
            } : {}),
            // Pagination footer styling - ensure it's not too far down
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${isDark ? alpha("#ffffff", 0.2) : alpha("#000000", 0.15)}`,
              backgroundColor: isDark ? alpha("#0d1b2a", 0.3) : alpha("#ffffff", 0.5),
              minHeight: "52px",
              paddingLeft: 2,
              paddingRight: 2,
              paddingTop: 1,
              paddingBottom: 1,
              marginTop: 0,
              marginBottom: 0,
              position: "sticky",
              bottom: 0,
              zIndex: 3,
              "& .MuiDataGrid-pagination": {
              },
              "& .MuiDataGrid-selectedRowCount": {
                color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
                fontWeight: 500,
              },
              "& .MuiTablePagination-root": {
                color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
              },
              "& .MuiTablePagination-selectLabel": {
                color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
                fontWeight: 500,
                fontSize: "0.875rem",
              },
              "& .MuiTablePagination-displayedRows": {
                color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
                fontWeight: 500,
                fontSize: "0.875rem",
              },
              "& .MuiTablePagination-actions": {
                "& button": {
                  color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
                  "&:hover": {
                    backgroundColor: isDark ? alpha("#ffffff", 0.1) : alpha("#000000", 0.05),
                  },
                  "&:disabled": {
                    color: isDark ? "rgba(255, 255, 255, 0.26)" : "rgba(0, 0, 0, 0.26)",
                  },
                },
              },
              "& .MuiTablePagination-select": {
                color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
                fontWeight: 500,
                "& .MuiTablePagination-selectIcon": {
                  color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
                },
              },
            },
            ...sx,
          }}
        />
      </Paper>
    );
  }
);

GlassDataGrid.displayName = "GlassDataGrid";

export default GlassDataGrid;

